/**
 * Softphone – moteur de la maquette : appels simulés dans l'onglet (aucun son, aucun réseau), pour développer et
 * montrer le panneau sans instance Amazon Connect. Chaque appel terminé est remis à `journaliser` (le panneau l'écrit
 * dans le journal de la maquette, source « simulation »), comme le fera le journal automatique de Connect.
 *   - appel sortant : numérotation pendant 1,5 s, puis le correspondant décroche ;
 *   - appel entrant : `simulerAppelEntrant(numero)` (agent disponible et libre) ; sans réponse en 20 s, il est manqué ;
 *   - transfert : l'appel quitte le softphone comme s'il avait été transféré ;
 *   - renvoi d'appel : seulement mémorisé et affiché (aucun téléphone ne sonne).
 */
import { creerDiffusion, type AppelSoftphone, type EtatAgent, type MoteurSoftphone } from './moteur';

export interface AppelTermine {
  sens: AppelSoftphone['sens'];
  numero: string | null;
  /** Début de la sonnerie ou de la numérotation (ISO 8601). */
  debut: string;
  dureeS: number;
  statut: 'repondu' | 'manque';
}

export interface MoteurMaquette extends MoteurSoftphone {
  simulerAppelEntrant(numero: string): void;
}

export const DELAIS_MAQUETTE = { connexion: 400, numerotation: 1500, sonnerie: 20_000, affichageFin: 2500 } as const;

export function creerMoteurMaquette(journaliser: (a: AppelTermine) => void): MoteurMaquette {
  const diffusion = creerDiffusion();
  let minuteur: ReturnType<typeof setTimeout> | undefined;
  let disponible = true;
  let numeroAppel = 0;
  let debut = 0;

  const programmer = (ms: number, f: () => void) => {
    clearTimeout(minuteur);
    minuteur = setTimeout(f, ms);
  };
  const appel = () => diffusion.lire().appel;
  const actif = () => { const a = appel(); return !!a && a.etat !== 'termine'; };
  const modifier = (m: Partial<AppelSoftphone>) => {
    const a = appel();
    if (a) diffusion.publier({ appel: { ...a, ...m } });
  };
  const etatLibre = (): EtatAgent => (disponible ? 'disponible' : 'absent');

  const commencer = (sens: AppelSoftphone['sens'], numero: string | null) => {
    numeroAppel += 1;
    debut = Date.now();
    diffusion.publier({
      agent: 'occupe',
      erreur: undefined,
      appel: { id: `maquette-${numeroAppel}`, sens, numero, etat: sens === 'entrant' ? 'sonnerie' : 'numerotation', enAttente: false, muet: false },
    });
  };

  const terminer = () => {
    const a = appel();
    if (!a || a.etat === 'termine') return;
    const fin = Date.now();
    journaliser({
      sens: a.sens, numero: a.numero, debut: new Date(debut).toISOString(),
      dureeS: a.decrocheLe ? Math.round((fin - a.decrocheLe) / 1000) : 0,
      statut: a.decrocheLe ? 'repondu' : 'manque',
    });
    diffusion.publier({ appel: { ...a, etat: 'termine', enAttente: false } });
    programmer(DELAIS_MAQUETTE.affichageFin, () => diffusion.publier({ agent: etatLibre(), appel: null }));
  };

  const decrocherMaintenant = () => {
    clearTimeout(minuteur);
    modifier({ etat: 'en-cours', decrocheLe: Date.now() });
  };

  return {
    demarrer() {
      programmer(DELAIS_MAQUETTE.connexion, () => diffusion.publier({ agent: etatLibre() }));
    },
    arreter() {
      clearTimeout(minuteur);
    },
    abonner: diffusion.abonner,
    connecter() {
      diffusion.publier({ agent: etatLibre(), erreur: undefined });
    },
    changerDisponibilite(oui) {
      disponible = oui;
      if (!actif()) diffusion.publier({ agent: etatLibre() });
    },
    appeler(numero) {
      if (actif()) return;
      commencer('sortant', numero);
      programmer(DELAIS_MAQUETTE.numerotation, decrocherMaintenant);
    },
    simulerAppelEntrant(numero) {
      if (actif() || !disponible) return;
      commencer('entrant', numero);
      programmer(DELAIS_MAQUETTE.sonnerie, terminer);
    },
    decrocher() {
      if (appel()?.etat === 'sonnerie') decrocherMaintenant();
    },
    raccrocher: terminer,
    basculerAttente() {
      const a = appel();
      if (a?.etat === 'en-cours') modifier({ enAttente: !a.enAttente });
    },
    basculerMuet() {
      const a = appel();
      if (a?.etat === 'en-cours') modifier({ muet: !a.muet });
    },
    envoyerTouches() {
      // Aucun serveur vocal dans la maquette.
    },
    transferer() {
      if (appel()?.etat === 'en-cours') terminer();
    },
    definirRenvoi(numero) {
      if (!actif()) diffusion.publier({ renvoi: numero, erreur: undefined });
    },
  };
}
